import { useMemo } from 'react';
import type { TFunction } from 'i18next';
import styled from 'styled-components';

import { fromDate, type CampaignPositionDto } from '@/services/ws-broadcast/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Link } from '@/js/libs/ui/src/V2';

import { FormattedNumber } from 'src/components/FormattedNumber';
import { DATE_FORMAT } from 'src/constants';
import { useColumnSwitch, type SwitchableColumns } from 'src/hooks/useColumnSwitch';
import { useBroadcastFiltersContext } from '../../context';
import type { Column, ColumnProps } from './types';

export const AMOUNT_COLUMNS = ['amountNet2', 'amountNet4'] as const satisfies readonly (keyof CampaignPositionDto)[];
export type AmountColumn = (typeof AMOUNT_COLUMNS)[number];
export const DEFAULT_AMOUNT_COLUMN: AmountColumn = 'amountNet2';

const Info = styled.em`
  display: block;
  word-break: break-word;
`;

interface UseColumnsProps {
  t: TFunction;
}

type UseColumns = (props: UseColumnsProps) => Column[];

const getColumns: UseColumns = ({ t }) => [
  {
    name: 'name',
    title: t('serviceDashboard:FLIGHT_NAME'),
    dataTestId: 'name-column',
    sortable: true,
    breakWord: true,
    fluid: true,
    component: ({ item }) => (
      <div>
        <Link to={`/orders/campaigns/ALL/${item.campaignId}`}>{item.campaignInternalOrderNumber}</Link>
        {item.campaignAdvertiser && ` – ${item.campaignAdvertiser}`}
        <br />
        {item.name}
        {item.positionComment && <Info>{item.positionComment}</Info>}
      </div>
    ),
  },
  {
    name: 'startDate',
    title: t('common:START_DATE'),
    dataTestId: 'start-date-column',
    sortable: true,
    component: ({ item }) => (item.startDate ? fromDate(item.startDate).format(DATE_FORMAT) : ''),
  },
  {
    name: 'endDate',
    title: t('common:END_DATE'),
    dataTestId: 'end-date-column',
    sortable: true,
    component: ({ item }) => (item.endDate ? fromDate(item.endDate).format(DATE_FORMAT) : ''),
  },
  {
    name: 'quantity',
    title: t('common:QUANTITY'),
    dataTestId: 'quantity-column',
    sortable: true,
    component: ({ item }) => item.quantity,
  },
  {
    name: 'amountOfSeconds',
    title: t('common:AMOUNT_OF_SECONDS'),
    dataTestId: 'amount-of-seconds-column',
    sortable: true,
    component: ({ item }) => <FormattedNumber format='float'>{item.amountOfSeconds}</FormattedNumber>,
  },
];

export const useColumns: UseColumns = ({ t }) => {
  const currentFilters = useBroadcastFiltersContext('filters$');
  const { updateFilter } = useBroadcastFiltersContext();

  const { amountColumn: selectedAmountColumn } = currentFilters;
  const handleAmountColumnChange = useCallbackRef((value: AmountColumn) => {
    updateFilter('amountColumn', value);
  });

  const amountColumns = useMemo(
    () =>
      [
        {
          name: 'amountNet2',
          title: t('serviceDashboard:AMOUNT_N2'),
          dataTestId: 'amount-net2-column',
          component: ({ item }) => (
            <FormattedNumber format='float' currency={item.adPrice.currency}>
              {item.amountNet2}
            </FormattedNumber>
          ),
        },
        {
          name: 'amountNet4',
          title: t('serviceDashboard:AMOUNT_N4'),
          dataTestId: 'amount-net4-column',
          component: ({ item }) => (
            <FormattedNumber format='float' currency={item.adPrice.currency}>
              {item.amountNet4}
            </FormattedNumber>
          ),
        },
      ] satisfies SwitchableColumns<AmountColumn, ColumnProps>,
    [t],
  );

  const amountColumn = useColumnSwitch({
    columns: amountColumns,
    defaultColumn: DEFAULT_AMOUNT_COLUMN,
    dataTestId: 'amount-column',
    selectedColumn: selectedAmountColumn,
    onColumnChange: handleAmountColumnChange,
  });

  return useMemo(() => [...getColumns({ t }), { ...amountColumn, sortable: true }], [t, amountColumn]);
};
